import { useMutation } from "@apollo/client";
import { compact } from "lodash";
import { useMemo, useState } from "react";
import { useCallback } from "react";
import {
  ComplianceProgramListItemFragment,
  ComplianceProgramState,
  DeleteProgramMutation,
  DeleteProgramMutationVariables,
  ExportComplianceProgramToCsvMutationMutation,
  ExportComplianceProgramToCsvMutationMutationVariables,
  GetComplianceProgramQuery,
  OrganizationRole,
  ProgramParticipantUpdatesFragment,
  SaveComplianceProgramMutation,
  SaveComplianceProgramMutationVariables,
} from "types/graphql-schema";

import {
  currentOrganizationVar,
  currentUserVar,
  successNotificationVar,
} from "@cache/cache";
import Dropdown from "@components/dropdown/dropdown";
import { removeConsecutiveDividers } from "@components/dropdown/dropdown-helpers";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import createOrUpdateComplianceProgramMutation from "../graphql/create-or-update-compliance-program-mutation";
import deleteComplianceProgramMutation from "../graphql/delete-compliance-program-mutation";
import exportComplianceProgramToCsvMutation from "../graphql/export-compliance-program-to-csv-mutation";
import getComplianceProgramQuery from "../graphql/get-compliance-program-admin-query";
import getComplianceProgramsQuery from "../graphql/get-compliance-programs-query";
import { useProgramsSection } from "../programs-section-context";
import ComplianceProgramDetailsDialog from "./compliance-program-details-dialog";
import ComplianceProgramUserChangesDialog from "./compliance-program-user-changes-dialog";

const ComplianceProgramActionDropdown = ({
  complianceProgram,
  availableAppliesToUpdates,
}: {
  complianceProgram:
    | NonNullable<GetComplianceProgramQuery["complianceProgram"]>
    | ComplianceProgramListItemFragment;
  availableAppliesToUpdates?: ProgramParticipantUpdatesFragment | null;
}) => {
  const link = useLink();
  const { basePath, listUrl, noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const isGlobalAdmin =
    organization.userMembership?.role === OrganizationRole.GlobalAdmin;

  const complianceProgramId = complianceProgram.id;
  const complianceProgramState = complianceProgram.state;
  const complianceProgramTitle = complianceProgram.title;
  const canUpdate = complianceProgram.canUpdate;
  const canDelete = complianceProgram.canDelete;

  const [
    isShowingProgramUserChangesDialog,
    setIsShowingProgramUserChangesDialog,
  ] = useState(false);
  const [isShowingProgramDetailsDialog, setIsShowingProgramDetailsDialog] =
    useState(false);

  const { ConfirmationDialog: ConfirmDeleteDialog, confirm: confirmDelete } =
    useConfirm(
      "Are you sure?",
      `Are you sure you want to delete this ${nounLower}? All assessment data will be deleted.`,
    );

  const [saveComplianceProgram, { loading: isPublishingProgram }] = useMutation<
    SaveComplianceProgramMutation,
    SaveComplianceProgramMutationVariables
  >(createOrUpdateComplianceProgramMutation);

  const [deleteProgram, { loading: isDeletingProgram }] = useMutation<
    DeleteProgramMutation,
    DeleteProgramMutationVariables
  >(deleteComplianceProgramMutation);

  const [exportComplianceProgramToCsv, { loading: isExportingCsv }] =
    useMutation<
      ExportComplianceProgramToCsvMutationMutation,
      ExportComplianceProgramToCsvMutationMutationVariables
    >(exportComplianceProgramToCsvMutation);

  const errorMatches = useMemo(
    () => [
      {
        match: "no actions to take",
        title: `Add at least one step to the ${nounLower} before publishing`,
      },
    ],
    [nounLower],
  );

  const publishProgram = useCallback(() => {
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Published,
        organizationId: organization.id,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery],
      onCompleted: () =>
        successNotificationVar({
          title: `${noun} published`,
        }),
    });
  }, [
    complianceProgramId,
    organization,
    errorMatches,
    saveComplianceProgram,
    noun,
  ]);

  const handleDeleteProgram = useCallback(async () => {
    const confirm = await confirmDelete();
    if (!confirm) return;
    deleteProgram({
      variables: {
        complianceProgramId,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramsQuery],
      onCompleted: () => {
        successNotificationVar({ title: `${noun} deleted` });
        link.redirect(listUrl);
      },
    });
  }, [
    confirmDelete,
    deleteProgram,
    complianceProgramId,
    errorMatches,
    link,
    listUrl,
    noun,
  ]);

  const handlePauseProgram = useCallback(async () => {
    saveComplianceProgram({
      variables: {
        complianceProgramId,
        state: ComplianceProgramState.Paused,
      },
      context: { errorMatches },
      refetchQueries: [getComplianceProgramQuery],
      onCompleted: () =>
        successNotificationVar({
          title: `${noun} paused`,
        }),
    });
  }, [saveComplianceProgram, complianceProgramId, errorMatches, noun]);

  const handleExportCsv = useCallback(() => {
    exportComplianceProgramToCsv({
      variables: {
        complianceProgramId,
      },
      onCompleted: () => {
        successNotificationVar({
          title: `CSV export will be emailed to ${currentUser.email}. The link will expire in two hours.`,
        });
      },
    });
  }, [complianceProgramId, currentUser.email, exportComplianceProgramToCsv]);

  const dropdownOptions: DropdownOptionType[] = useMemo(() => {
    return removeConsecutiveDividers(
      compact([
        {
          label: `Duplicate ${nounLower}`,
          onClick: () =>
            link.redirect(`${basePath}/${complianceProgramId}/duplicate`),
          disabled: isPublishingProgram || isDeletingProgram || isExportingCsv,
        },
        canUpdate?.permission && {
          label: `Edit ${nounLower}`,
          onClick: () =>
            link.redirect(`${basePath}/${complianceProgramId}/edit`),
          disabled: isPublishingProgram || isDeletingProgram || isExportingCsv,
        },
        canUpdate?.permission &&
          complianceProgramState === ComplianceProgramState.Published && {
            label: `Pause ${nounLower}`,
            onClick: handlePauseProgram,
            disabled:
              isPublishingProgram || isDeletingProgram || isExportingCsv,
          },
        canUpdate?.permission &&
          complianceProgramState !== ComplianceProgramState.Published && {
            label:
              complianceProgramState === ComplianceProgramState.Draft
                ? "Publish"
                : "Resume",
            onClick: publishProgram,
            disabled: isPublishingProgram || isExportingCsv,
          },
        isGlobalAdmin && {
          label: `Export ${nounLower} data`,
          onClick: handleExportCsv,
          disabled: isPublishingProgram || isDeletingProgram || isExportingCsv,
        },
        { type: "divider" },
        availableAppliesToUpdates?.hasUpdates && {
          label: `Update ${noun} Participants`,
          onClick: () => setIsShowingProgramUserChangesDialog(true),
        },
        {
          label: `View ${noun} Details`,
          onClick: () => setIsShowingProgramDetailsDialog(true),
        },
        { type: "divider" },
        canDelete?.permission && {
          label: `Delete ${nounLower}`,
          onClick: handleDeleteProgram,
          textColor: "text-red-700 hover:text-red-900",
          disabled: isPublishingProgram || isDeletingProgram || isExportingCsv,
        },
      ]),
    );
  }, [
    basePath,
    canDelete,
    canUpdate,
    complianceProgramId,
    complianceProgramState,
    availableAppliesToUpdates,
    handleDeleteProgram,
    handleExportCsv,
    handlePauseProgram,
    isGlobalAdmin,
    isDeletingProgram,
    isExportingCsv,
    isPublishingProgram,
    link,
    publishProgram,
    noun,
    nounLower,
  ]);

  if (dropdownOptions.length === 0) return null;

  return (
    <>
      <Dropdown
        options={dropdownOptions}
        align="end"
        gap={8}
        aria-label="Compliance program actions"
      />
      <ConfirmDeleteDialog />
      {isShowingProgramUserChangesDialog && availableAppliesToUpdates && (
        <ComplianceProgramUserChangesDialog
          onClose={() => setIsShowingProgramUserChangesDialog(false)}
          complianceProgramId={complianceProgramId}
          complianceProgramTitle={complianceProgramTitle}
          availableAppliesToUpdates={availableAppliesToUpdates}
        />
      )}
      {isShowingProgramDetailsDialog && (
        <ComplianceProgramDetailsDialog
          complianceProgram={complianceProgram}
          onClose={() => setIsShowingProgramDetailsDialog(false)}
        />
      )}
    </>
  );
};
export default ComplianceProgramActionDropdown;
